import {
  BALANCE,
  REFERENCE_SOLVE_MS,
  edgeBetween,
  gearCost,
  ropeHops,
  validatePlan,
  type Detour,
  type EntranceId,
  type GearId,
  type MapPublic,
  type Plan,
} from '@il-colpo/engine';

/**
 * The mastermind's plan under construction. It works on the public shape of the map and the
 * per-room estimates the mastermind is given, so it needs no puzzle pools; the engine checks
 * the plan again when it is proposed.
 */

export interface Draft {
  entrance: EntranceId | null;
  route: string[];
  detours: Detour[];
  gear: GearId[];
}

export const EMPTY_DRAFT: Draft = { entrance: null, route: [], detours: [], gear: [] };

export const ALL_GEAR: readonly GearId[] = [
  'proPick',
  'jammer',
  'rope',
  'disguise',
  'drone',
  'emp',
];

export function fromPlan(plan: Plan): Draft {
  return {
    entrance: plan.entrance,
    route: [...plan.route],
    detours: plan.detours.map((d) => ({ ...d })),
    gear: [...plan.gear],
  };
}

export function toPlan(draft: Draft): Plan | null {
  if (!draft.entrance) return null;
  return {
    entrance: draft.entrance,
    route: [...draft.route],
    detours: draft.detours.map((d) => ({ ...d })),
    gear: [...draft.gear],
  };
}

export function chooseEntrance(map: MapPublic, draft: Draft, entrance: EntranceId): Draft {
  const room = map.entrances[entrance];
  return { ...draft, entrance, route: room ? [room] : [], detours: [] };
}

const kindOf = (map: MapPublic, room: string): string | undefined =>
  map.rooms.find((r) => r.id === room)?.kind;

/** Rooms next to `room`, by a passage or (with the rope in the kit) a rope link. */
function reach(map: MapPublic, room: string, withRope: boolean): string[] {
  const out: string[] = [];
  for (const e of map.edges) {
    if (e.a === room) out.push(e.b);
    else if (e.b === room) out.push(e.a);
  }
  if (withRope) {
    for (const [a, b] of map.ropeLinks) {
      if (a === room) out.push(b);
      else if (b === room) out.push(a);
    }
  }
  return out;
}

/** What a tap on a room does: extend the route, cut it back, or toggle an alcove. */
export function pressRoom(map: MapPublic, draft: Draft, room: string): Draft {
  if (!draft.entrance || draft.route.length === 0) return draft;
  const last = draft.route[draft.route.length - 1] as string;

  if (kindOf(map, room) === 'optional') {
    const existing = draft.detours.findIndex((d) => d.room === room);
    if (existing >= 0) {
      return { ...draft, detours: draft.detours.filter((_, i) => i !== existing) };
    }
    if (draft.detours.length >= BALANCE.maxDetours) return draft;
    const anchor = draft.route.find((r) => reach(map, room, false).includes(r));
    return anchor ? { ...draft, detours: [...draft.detours, { anchor, room }] } : draft;
  }

  const at = draft.route.indexOf(room);
  if (at >= 0) {
    // Tapping a room already on the route cuts the route back to it (but never the entrance).
    const cut = at === 0 ? 1 : at + 1;
    const route = draft.route.slice(0, cut);
    return { ...draft, route, detours: draft.detours.filter((d) => route.includes(d.anchor)) };
  }
  if (draft.route.length >= BALANCE.routeMaxRooms) return draft;
  if (!reach(map, last, true).includes(room)) return draft;
  return { ...draft, route: [...draft.route, room] };
}

export function toggleGear(draft: Draft, gear: GearId): Draft {
  return draft.gear.includes(gear)
    ? { ...draft, gear: draft.gear.filter((g) => g !== gear) }
    : { ...draft, gear: [...draft.gear, gear] };
}

/** Rooms that make sense to tap next. */
export function selectableRooms(map: MapPublic, draft: Draft): Set<string> {
  const out = new Set<string>();
  if (!draft.entrance || draft.route.length === 0) return out;
  const last = draft.route[draft.route.length - 1] as string;
  if (last !== map.vault && draft.route.length < BALANCE.routeMaxRooms) {
    for (const n of reach(map, last, true)) {
      if (kindOf(map, n) !== 'optional' && !draft.route.includes(n)) out.add(n);
    }
  }
  for (const r of draft.route) {
    out.add(r);
    for (const n of reach(map, r, false)) if (kindOf(map, n) === 'optional') out.add(n);
  }
  return out;
}

/**
 * Why the draft is not a plan yet (a code the screen translates), or null if it is one. The
 * common mistakes get their own code; anything else is the engine's own verdict.
 */
export function draftProblem(map: MapPublic, draft: Draft): string | null {
  const plan = toPlan(draft);
  if (!plan) return 'noEntrance';
  if (plan.route[plan.route.length - 1] !== map.vault) return 'noVault';
  if (plan.route.length < BALANCE.routeMinRooms) return 'tooShort';
  if (ropeHops(map, plan.route) > 0 && !plan.gear.includes('rope')) return 'needRope';
  if (gearCost(plan.gear) > BALANCE.gearBudget) return 'budget';
  return validatePlan(map, plan) === null ? null : 'other';
}

export function gearSpent(draft: Draft): number {
  return gearCost(draft.gear);
}

/**
 * Time the draft needs, from the mastermind's per-room estimates; it follows
 * `estimatePlanMs` in the engine (a test keeps the two together).
 */
export function estimateDraftMs(
  map: MapPublic,
  roomEstimate: Record<string, number>,
  draft: Draft,
): number {
  let total = 0;
  draft.route.forEach((room, i) => {
    total += room === map.vault ? REFERENCE_SOLVE_MS.vault : (roomEstimate[room] ?? 0);
    const next = draft.route[i + 1];
    if (next) {
      const e = edgeBetween(map, room, next);
      if (e?.guarded) total += REFERENCE_SOLVE_MS.corridor;
      else if (!e) total += BALANCE.ropeHopMs;
    }
  });
  for (const d of draft.detours) {
    total += (roomEstimate[d.room] ?? 0) + BALANCE.detourReturnMs;
    if (edgeBetween(map, d.anchor, d.room)?.guarded) total += REFERENCE_SOLVE_MS.corridor;
  }
  total += REFERENCE_SOLVE_MS.escape;
  if (draft.entrance === 'roof') total += BALANCE.roofTimerCostMs;
  return total;
}
